import { ReactNode, useEffect, useRef } from 'react';

// The tab list is the single source of truth for both the nav and the `?tab=`
// values JobDetail parses, so a new tab can't be added to one and missed in the
// other.
export const JOB_DETAIL_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'breakdown', label: 'Breakdown' },
  { id: 'expenses', label: 'Expenses' },
  { id: 'billing', label: 'Billing' },
  { id: 'inspections', label: 'Inspections' },
  { id: 'files', label: 'Files' },
] as const;

export type Tab = (typeof JOB_DETAIL_TABS)[number]['id'];

// Breathing room left either side of the active tab once it is scrolled into
// view, so it never sits flush against the clipped edge.
const SCROLL_PADDING = 12;

interface JobDetailTabsProps {
  activeTab: Tab;
  onTabChange: (tab: Tab) => void;
  leftContent?: ReactNode;
  rightContent?: ReactNode;
}

export function JobDetailTabs({
  activeTab,
  onTabChange,
  leftContent,
  rightContent,
}: JobDetailTabsProps) {
  const tabs = JOB_DETAIL_TABS;
  const navRef = useRef<HTMLElement | null>(null);
  const tabRefs = useRef<Partial<Record<Tab, HTMLButtonElement | null>>>({});

  // Six tabs don't fit a 390px viewport, so the strip scrolls — which means a
  // deep link to a later tab can land with its own tab off-screen. Nudge the
  // strip rather than calling scrollIntoView: that walks every scrollable
  // ancestor and would drag the whole page sideways.
  useEffect(() => {
    const nav = navRef.current;
    const button = tabRefs.current[activeTab];
    if (!nav || !button) return;

    const left = button.offsetLeft;
    const right = left + button.offsetWidth;

    if (left < nav.scrollLeft) {
      nav.scrollLeft = Math.max(0, left - SCROLL_PADDING);
    } else if (right > nav.scrollLeft + nav.clientWidth) {
      nav.scrollLeft = right - nav.clientWidth + SCROLL_PADDING;
    }
  }, [activeTab]);

  return (
    // Mobile stacks this into two rows via wrapping: the meta (Back, job code,
    // client) and the status badges share the first row, and the full-width nav
    // is pushed onto a second. From `sm` up the three sit on one nowrap row in
    // the original order — meta, tabs, badges hard right.
    <div className="border-b border-gray-200 mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 sm:flex-nowrap sm:gap-x-6">
      {leftContent && (
        <div className="flex min-w-0 items-center gap-3 sm:shrink-0 sm:gap-4">
          {leftContent}
          <span className="hidden h-5 w-px bg-gray-200 sm:block" aria-hidden="true" />
        </div>
      )}
      <div className="order-2 flex flex-wrap items-center gap-2 sm:order-3 sm:shrink-0">
        {rightContent}
      </div>
      <nav
        ref={navRef}
        // `relative` is load-bearing: offsetLeft is measured against the nearest
        // positioned ancestor, which has to be the scroll container itself for
        // the scroll-into-view effect above to compute the right offsets.
        className="relative -mb-px order-3 flex w-full gap-4 overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:order-2 sm:mr-auto sm:w-auto sm:overflow-x-visible"
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            ref={(el) => { tabRefs.current[tab.id] = el; }}
            onClick={() => onTabChange(tab.id)}
            className={`shrink-0 whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium ${
              activeTab === tab.id
                ? 'border-blue-500 text-blue-600 font-semibold'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </nav>
    </div>
  );
}
